import { CSS_URL, ENDPOINT, EXT_ENDPOINT } from 'consts/api';
import {
	BROWSER_AGENT_REGEX,
	BROWSER_AGENT_REGEX_TYPE,
	GENERIC_STRINGS,
	GENERIC_STRINGS_ARRAY,
	INSTANCE_DOMAIN_URL,
} from 'consts/consts';
import { BOT_MODE } from 'consts/enum';
import { LS } from 'consts/localStorage';
import { api } from 'helpers/api';
import {
	callbackOnKeyDown,
	changeElementStyleAttr,
	debug,
	getParamsFromUrl,
	getStandaloneUrlParams,
	getWidgetUrlParams,
	initXMLhttp,
	isLocalHost,
	isValidResponse,
	isValidResponseData,
	LOGGER,
	merge,
	readFromLs,
	removeClass,
	resetNotificationCount,
	updateBotLanguageToLS,
	writeToLs,
} from 'helpers/helpers';
import { fetchUserMessages } from 'helpers/socket';
import { ENGT_CHAT } from 'js/bot.model';
import { onConfigBotResponse } from 'js/service';

import { updateBotLanguage } from './helpers/helpers';
import { appendStatusDivider } from './js/conversation';

const getWidgetAttributes = (config) => {
	let attributes = [];
	if (config.initFunction) {
		const getUserAttributes = new Function(config.initFunction);
		attributes = getUserAttributes();
	}

	if (config?.user_attributes?.length) {
		attributes = [...attributes, ...config.user_attributes];
	}

	return [].concat(attributes);
};

const getStandaloneAttributes = () => {
	const attributes = [];
	const data_attrs = getParamsFromUrl('data_attrs');

	if (data_attrs) {
		const getAttrsArray = data_attrs.replace(/[{}]/g, '').split('::');
		for (let i = 0; i < getAttrsArray.length; i++) {
			const data = getAttrsArray[i].split('=');
			const item = { name: data[0], value: data[1] };
			attributes.push(item);
		}
	}

	return [].concat(attributes);
};

const setConfigFromParams = (mode = BOT_MODE.WIDGET) => {
	let urlParams;
	let config = {};

	if (mode === BOT_MODE.WIDGET) {
		urlParams = getWidgetUrlParams('widget', 'chat_widget');
		config = urlParams.config ? JSON.parse(decodeURIComponent(urlParams.config)) : {};
		config.user_attributes = [...getWidgetAttributes(config)];
	} else {
		urlParams = getStandaloneUrlParams();
		config = { ...urlParams.config };
		config.user_attributes = [...getStandaloneAttributes()];
	}

	let deferredIpAddressPromise;
	const ipAddressPromise = new Promise((resolve, reject) => {
		deferredIpAddressPromise = { resolve, reject };
	});

	ENGT_CHAT.set(
		'config',
		merge(
			{
				user: 'guest',
				e: 'p',
				server: INSTANCE_DOMAIN_URL,
				brandedServer: config.server || INSTANCE_DOMAIN_URL,
				bot_name: 'Engati',
				initFunction: '',
				repeatFlowOnClick: '',
				website_url: '',
				themecolor: '#19BE6E',
				bot_key: undefined,
				icon_url: undefined,
				chat_widget_position: 'bottomright',
				user_attributes: null,
				retryInterval: 5000,
				maxRetry: 3,
				remove_branding: false,
				use_icon_dimensions: false,
				launch_flow: undefined,
				launch_flow_copy: undefined,
				refresh_chat: false,
				brand_name: 'Engati',
				key: '',
				set_default_brand: false,
				show_password_input: false,
				textAreaPlaceholder: 'Write a reply...',
				header_description: null,
				autoPopupType: 'NONE',
				is_debug: false,
				setType: 1,
				livechatEnabled: false,
				notificationCount: 1,
				infocus: false,
				agentAvatarCount: 1,
				fontSize: '13px',
				enableIpAddress: false,
				override_pop_up: false,
				highlightColor: 'E50F29',
				storeUserResponseInLs: true,
				sessionExpired: true,
				platform: 'web',
				video_conversation_id: '',
				end_user_name: '',
				agent_name: '',
				video_status: '',
				room_name: '',
				access_token: '',
				user_id: '',
				isHostedBot: false,
				isChatOpened: false,
				chatMessageTimestamp: null,
				userIpAddress: null,
				ipAddressPromise,
				deferredIpAddressPromise,
				bot_mode: mode,
				isFaqAcPopUpClose: false,
				processingInputText: 'Processing input...',
			},
			config
		)
	);
};

const setServerDetails = (resp) => {
	const instance = resp.instance_domain_url;
	const config = ENGT_CHAT.get('config');
	let server = '';
	let fileUploadUrl = '';
	let faqAutocompleteBaseUrl = '';
	let voiceLLMBaseUrl = '';
	const brandedServerUrl =
		ENGT_CHAT.get('config.bot_mode') === BOT_MODE.STANDALONE && !isLocalHost()
			? `${window.location.protocol}//${window.location.host}`
			: instance;

	if (config.e === 'd') {
		server = instance;
		fileUploadUrl = instance;
		faqAutocompleteBaseUrl = instance;
		voiceLLMBaseUrl = instance;
	} else if (config.e === 'p') {
		server = instance;
		fileUploadUrl = 'https://upload.engati.com';
		faqAutocompleteBaseUrl = instance;
		voiceLLMBaseUrl = instance;
	} else if (config.e === 'l') {
		server = 'http://localhost:5010';
		fileUploadUrl = 'http://localhost:5010';
		faqAutocompleteBaseUrl = 'https://localhost:7700/';
		voiceLLMBaseUrl = 'https://localhost:8000/';
	} else if (config.e === 'qa') {
		server = instance;
		fileUploadUrl = instance;
		faqAutocompleteBaseUrl = instance;
		voiceLLMBaseUrl = instance;
	} else if (config.e === 'staging') {
		server = 'https://staging.engati.com';
		fileUploadUrl = 'https://staging.engati.com';
		faqAutocompleteBaseUrl = 'https://qa.engati.com/';
		voiceLLMBaseUrl = 'https://qa.engati.com/';
	} else if (config.e === 'op') {
		server = config.server;
		fileUploadUrl = config.server;
		faqAutocompleteBaseUrl = config.server;
		voiceLLMBaseUrl = config.server;
	}

	ENGT_CHAT.set('config.server', server);
	ENGT_CHAT.set('config.brandedServer', brandedServerUrl);
	ENGT_CHAT.set('fileUploadBaseURL', fileUploadUrl);
	ENGT_CHAT.set('faqAutocompleteBaseUrl', faqAutocompleteBaseUrl);
	ENGT_CHAT.set('voiceLLMBaseUrl', voiceLLMBaseUrl);
};

const setBrandingKey = (resp) => {
	const { instance_domain_url } = resp || {};
	ENGT_CHAT.set('config.branding_key', resp.branding_key || instance_domain_url);
};

const setVoiceLocale = (langCode) => {
	const userPreferences = JSON.parse(localStorage[LS().USER_PREFERENCE] || '{}');

	ENGT_CHAT.set('userLocale', userPreferences['voice_locale'] || langCode);

	writeToLs(
		LS().USER_PREFERENCE,
		JSON.stringify({
			voice_locale: userPreferences['voice_locale'] || langCode,
		})
	);
};

const setGenericStrings = (genericStringsResponse) => {
	const launchCard = document.getElementById('startNewConversationBtn');
	const writeAReply = document.getElementById('engtUserMessage');

	let genericStringMap = [];
	if (genericStringsResponse.status) {
		genericStringMap = genericStringsResponse.resp;
		for (const key in genericStringMap) {
			const genericStringsKey = key.split('SETTINGS.')[1];
			GENERIC_STRINGS[genericStringsKey] = genericStringMap[key];
		}
		ENGT_CHAT.set('GENERIC_STRINGS', GENERIC_STRINGS);
	} else {
		debug(`error in genericStrings api ${genericStringsResponse}`);
	}
};

const setIpAddress = (data) => {
	const ipAddress = data.ip;
	if (ipAddress) {
		ENGT_CHAT.get('config.deferredIpAddressPromise').resolve(ipAddress);
		ENGT_CHAT.set('config.userIpAddress', ipAddress);
	} else {
		ENGT_CHAT.get('config.deferredIpAddressPromise').reject('Unable to fetch IP Address');
	}
};

const getBrowserAgent = async () => {
	(function (a) {
		if (BROWSER_AGENT_REGEX.test(a) || BROWSER_AGENT_REGEX_TYPE.test(a.substr(0, 4))) {
			ENGT_CHAT.set('isMobileOrTablet', true);
		}
	})(navigator.userAgent || navigator.vendor || window.opera);
};

export const getGenericStrings = () =>
	api('POST', ENDPOINT().GENERIC_STRING, {
		payload: JSON.stringify(GENERIC_STRINGS_ARRAY),
	})
		.then((data) => {
			isValidResponseData(data) && JSON.parse(data.responseText);
			setGenericStrings(JSON.parse(data.responseText));
			setLocalizedString();
		})
		.catch((error) => {
			debug(error);
		});

export const getCssFile = async () =>
	new Promise((resolve) => {
		const c = document.head || document.getElementsByTagName('head')[0];
		const m = document.createElement('link');
		m.async = !0;
		m.defer = !0;
		m.rel = 'stylesheet';
		m.type = 'text/css';
		m.href = CSS_URL();
		m.onload = resolve;
		c.appendChild(m);
	});

const getInstance = async () =>
	api('GET', ENDPOINT().INSTANCE)
		.then((resp) => isValidResponse(resp) && setServerDetails(JSON.parse(resp.responseText)))
		.catch((e) => console.error('index.js: exception in get instance api', e));

const getBrandingKey = async () =>
	api('GET', ENDPOINT().BRANDING_KEY)
		.then((resp) => isValidResponse(resp) && setBrandingKey(JSON.parse(resp.responseText)))
		.catch((e) => console.error('index.js: exception in get branding key api', e));

const getChatConfig = async () =>
	api('GET', ENDPOINT().CHAT_CONFIG)
		.then((resp) => (isValidResponseData(resp) ? JSON.parse(resp.responseText).data : {}))
		.catch((e) => console.error('index.js: exception in get chat config api', e));

const getWidgetconfig = async () =>
	api('GET', ENDPOINT().WIDGET_CONFIG)
		.then((resp) => (isValidResponseData(resp) ? JSON.parse(resp.responseText).data : {}))
		.catch((e) => console.error('index.js: exception in get widget config api', e));

const getIpAddress = async (enable_ip_address = false) => {
	if (enable_ip_address) {
		try {
			const http = initXMLhttp();
			http.open('GET', EXT_ENDPOINT().IP_ADDRESS, true);
			http.onreadystatechange = function () {
				let resp;
				try {
					resp = JSON.parse(http.responseText);
				} catch (e) {
					resp = http.responseText;
				}

				if (this.readyState === 4) {
					if (this.status === 200) {
						if (resp?.ip) {
							setIpAddress(resp);
						} else {
							ENGT_CHAT.get('config.deferredIpAddressPromise').reject('Unable to fetch IP Address');
						}
					}
				}
			};
			http.send();
		} catch (err) {
			console.error('exception in get ip address api ', err);
			ENGT_CHAT.get('config.deferredIpAddressPromise').reject('Unable to fetch IP Address');
		}
	}
};

export const getBotConfig = () =>
	Promise.all([getWidgetconfig(), getChatConfig()]).then(
		(values) => {
			const [chatConfig, widgetConfig, genericStrings] = values || [];
			const data = merge(chatConfig, widgetConfig, genericStrings);

			onConfigBotResponse(data);
			return data;
		},
		(error) => {
			console.error(error.message);
		}
	);

export const getGenericStringsAndBotConfig = () => {
	getGenericStrings();
	getChatConfig().then((data) => {
		ENGT_CHAT.set('config.header_description', data.header_description);
		ENGT_CHAT.set('config.bot_name', data.bot_name);
	});
};

export const setSessionExpiredMsg = () => {
	const previewHeader = document.getElementById('launchPreviewHeader');
	const previewContainer = document.getElementById('launchPreview');
	const askQueryField = document.getElementById('engtQueryInput');

	if (previewHeader) {
		removeClass(previewContainer, 'engt-launch-preview-hidden');
		previewHeader.innerText = GENERIC_STRINGS.CHAT_CLOSURE_MESSAGE;
		if (askQueryField) {
			askQueryField.disabled = true;
		}

		if (previewHeader) {
			previewHeader.innerHTML = GENERIC_STRINGS?.CHAT_CLOSURE_MESSAGE;
		}
	}
	ENGT_CHAT.set('triggerSessionExpired', true);
	if (readFromLs(LS().GOT_UID) === 'false') {
		ENGT_CHAT.set('config.sessionExpired', true);
	}

	appendStatusDivider({ id: 'engtSessionTimeout', status: GENERIC_STRINGS.CHAT_CLOSURE_MESSAGE });
	ENGT_CHAT.get('renderStartConversation')();
	writeToLs(
		LS().TAB_MESSAGES,
		JSON.stringify({
			action: 'enable_session_timeout',
			tabId: ENGT_CHAT.get('socketId'),
			statusData: { id: 'engtSessionTimeout', status: GENERIC_STRINGS.CHAT_CLOSURE_MESSAGE },
		})
	);
	ENGT_CHAT.set('startConvoAdded', true);
	ENGT_CHAT.set('firstTime', true);
};

const setLocalizedStringForLaunchScreen = () => {
	const GENERIC_STRINGS = ENGT_CHAT.get('GENERIC_STRINGS');
	const launchCardStartNewConvoBtn = document.getElementById('startNewConversationBtn');
	const botName = document.getElementById('botName');
	const botDescription = document.getElementById('botDescription');
	const languageDropdown = document.getElementById('languageDropdown');
	const launchQueryTitle = document.getElementById('launchQueryTitle');
	const engtQueryInput = document.getElementById('engtQueryInput');
	const previewHeader = document.getElementById('launchPreviewHeader');
	const goBackConversation = document.getElementById('goBackConversation');
	const readMore = document.getElementsByClassName('engt-read-more-text');

	const launchCardsWrapper = document.getElementById('engtLaunchCards');
	const launchCards = launchCardsWrapper?.children?.[1];

	if (launchCards) {
		ENGT_CHAT.get('reRenderLaunchScreen')(true);
	}

	if (previewHeader) {
		previewHeader.innerText =
			GENERIC_STRINGS?.['LAUNCH.PREVIEW_HEADER'] || "Here's a preview of your ongoing conversation";
	}

	if (launchCardStartNewConvoBtn && GENERIC_STRINGS?.['LAUNCH.START_A_NEW_CONVO']) {
		launchCardStartNewConvoBtn.innerHTML = GENERIC_STRINGS['LAUNCH.START_A_NEW_CONVO'];
	}

	if (botName) {
		botName.innerHTML = ENGT_CHAT.get('config.launchScreenHeader');
	}

	if (botDescription) {
		botDescription.innerHTML = ENGT_CHAT.get('config.launchScreenTitle');
	}

	if (languageDropdown) {
		const botLanguage = readFromLs(LS().USER_LOCALE) || ENGT_CHAT.get('config.botLanguage') || 'en';
		updateBotLanguage(botLanguage);
		updateBotLanguageToLS(botLanguage);
	}

	if (launchQueryTitle) {
		launchQueryTitle.innerHTML = ENGT_CHAT.get('config.queryTitle');
	}

	if (engtQueryInput) {
		engtQueryInput.setAttribute('placeholder', ENGT_CHAT.get('config.queryPlaceholder'));
	}

	if (goBackConversation && GENERIC_STRINGS?.['LAUNCH.GO_BACK_TO_CONVERSATION']) {
		goBackConversation.innerText = GENERIC_STRINGS?.['LAUNCH.GO_BACK_TO_CONVERSATION'] || 'Go back to conversation';
	}

	/* fetch explore more element after launch card has been re rendered */
	const exploreMore = document.getElementById('exploreMore');
	if (exploreMore && GENERIC_STRINGS?.['EXPLORE_MORE']) {
		exploreMore.innerText = `${GENERIC_STRINGS?.['EXPLORE_MORE'] || 'Explore more on'} ${
			ENGT_CHAT.get('config.bot_name') || ENGT_CHAT.get('config.launchScreenHeader')
		}`;
	}

	if (readMore?.length && GENERIC_STRINGS?.['READ_MORE']) {
		for (let i = 0; i < readMore.length; i++) {
			if (readMore[i]?.innerText) {
				readMore.innerText = GENERIC_STRINGS?.['READ_MORE'] || 'Read More';
			}
		}
	}
};

export const setLocalizedString = () => {
	const writeAReply = document.getElementById('engtUserMessage');
	const launchScreenEnabled = ENGT_CHAT.get('config.isLaunchScreenEnabled') || undefined;
	const engtChatScreenHeader = document.getElementById('engtChatScreenHeader');
	const headerDescripton = document.querySelector('.engt-header-info-description');
	const genericStrings = ENGT_CHAT.get('GENERIC_STRINGS');
	const closeChatBody = document.getElementById('engtHeaderBody');
	const closeChatBtn = document.getElementById('engtHeaderCloseBtn');
	const exitAgentBtn = document.getElementById('exitLiveChatBtn');

	if (genericStrings) {
		if (closeChatBody) {
			closeChatBody.innerHTML = genericStrings?.CLOSE_CHAT_PROMPT || 'Close chat prompt';
		}
		if (closeChatBtn) {
			closeChatBtn.innerHTML = genericStrings?.CLOSE_CHAT || 'Close Chat';
		}
		if (exitAgentBtn) {
			exitAgentBtn.innerHTML = genericStrings?.EXIT_AGENT_CONVERSATION;
		}
	}

	if (launchScreenEnabled) {
		setLocalizedStringForLaunchScreen();
	}

	if (writeAReply) {
		writeAReply.setAttribute('data-placeholder', ENGT_CHAT.get('config.textAreaPlaceholder'));
	}

	if (engtChatScreenHeader) {
		engtChatScreenHeader.innerHTML = ENGT_CHAT.get('config.bot_name');
	}
	if (headerDescripton) {
		headerDescripton.textContent = ENGT_CHAT.get('config.header_description');
	}
};

const checkAndfetchMessages = (lastMessageTime) => {
	const messageContainer = document.querySelector('#engtChatContent');
	if (
		messageContainer.scrollTop === 0 &&
		!ENGT_CHAT.get('config.launch_flow_copy') &&
		!ENGT_CHAT.get('refreshChat')
	) {
		const chatContainer = document.getElementById('engtChatContent');
		ENGT_CHAT.set('lastMsgScrollHeight', chatContainer.scrollHeight);
		fetchUserMessages(lastMessageTime);
	}
};

const setCommonHelpers = () => {
	ENGT_CHAT.set('onEnterPress', callbackOnKeyDown);
	ENGT_CHAT.set('changeElementStyleAttr', changeElementStyleAttr);
	ENGT_CHAT.set('checkAndfetchMessages', checkAndfetchMessages);
};

export const init = async (mode) => {
	if (mode === BOT_MODE.WIDGET && !getWidgetUrlParams('widget', 'chat_widget')?.config) {
		return;
	}

	resetNotificationCount();
	setConfigFromParams(mode);

	setCommonHelpers();
	await getBrowserAgent();
	await getCssFile();
	await getInstance();
	if (mode === BOT_MODE.STANDALONE && !isLocalHost()) {
		await getBrandingKey();
	}

	try {
		await getBotConfig().then(async (data) => {
			getIpAddress(data.enable_ip_address);
			await getGenericStrings();
		});
	} catch (error) {
		LOGGER('Bot Config apis failed', error);
	}
};
